import { ContactForm as ContactFormModel } from '@models/MLTC_R3/Contact.model';
import Button from '@components/_Revamp/Button';
import { Checkbox } from '@components/_Revamp/Checkbox';
import { InputGroup } from '@components/_Revamp/InputGroup';
import { TitleAndDescription } from '@components/_Revamp/TitleAndDescription/TitleAndDescription';
import { styled } from '@linaria/react';
import useForm from '@utils/useForm';
import React, { useEffect, useState } from 'react';
import { ITheme, themeDefault, useTheme } from 'src/theme-revamp';
import { validate } from './service';
import { useAvivaDispatch, useAvivaSelector } from '@redux/hook';
import { get, isEmpty } from 'lodash';
import { updateContact } from '@redux/slices/MLTC_R3/contactSlice';
import { SubmitSuccess } from './components/SubmitSuccess';
import { setLoadingState } from '@redux/slices/loadingSlice';
import { SQSPocketResponse } from '@models/MLTC/SQSPocketRequest.model';
import { Helpers, Validations } from '@utils/helpers';
import { postContactCIMBMltc, postContactMltc } from '@api/index';
import { GAAviva } from '@utils/gaAvivaR3';
import { useHistory } from 'react-router-dom';
import Link from '@components/_Revamp/Link';
import { CIMB_Code } from '@static/campaign-code';
import Contactus from '@components/_Shared/Contactus';
import { CONTAINER_SIZE, SCREEN_SIZE } from '@static/screen-size';

const DataUsageDescription = styled.p<{ theme: ITheme }>`
  color: ${(props) => props.theme.colors.grey[1400]};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
`;

const AllowDataUsageContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: 16px;
`;

const SubmitNote = styled.p<{ theme: ITheme }>`
  color: ${(props) => props.theme.colors.grey[1400]};
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 500;
  line-height: 25px;
  letter-spacing: 0em;
  text-align: left;
  span {
    color: ${(props) => props.theme.colors.primary};
    font-weight: 500;
  }
  a{
    font-weight: 600;
    font-size: 14px;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    padding: 0 24px;
  }
`;

const FormActionContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 24px;
  margin-bottom: 64px;
`;

const ContactInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 48px;
  margin-bottom: 64px;
`;


const ContacFormTitleContainer = styled.div`
  max-width: 327px;
  margin: 100px auto 0 auto;
  @media screen and (min-width: 786px) {
    margin: 150px auto 0 auto;
    max-width: 662px;
  }
`;

const ContacFormContainer = styled.div`
  margin: 0 auto 50px auto;
  padding: 0 24px;

  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    max-width: ${CONTAINER_SIZE.desktops}px;
  }
`;

export const createContactRequest = (
  form: ContactFormModel,
  isVulnerable: boolean | undefined,
  transactionId: number | string,
  isRental: boolean | undefined,
  campaignCode: string | undefined,
  productName: string | undefined,
  agentCode: string | null = null,
  isEBLTC: boolean,
  companyName: string
  ) => {
  const content = {
    email: form.emailId,
    fname: form.fullname,
    lname: "NA",
    title: Helpers.getSalutationByCode(form.salutation || ''),
    dateOfBirth: form.dob,
    phone_number: form.mobileNum,
    pdpaDetails: "{}",
    selectedPlan: productName,
    VulnerableCustomer: isVulnerable ? 'true' : 'false',
    RentalPostalCustomer: isRental ? 'true' : 'false',
    referrerDetails: `${window.location.origin}/api/contactUs?cc=${campaignCode ? campaignCode : 'MLO1'}`,
    nationalIdentifier: form.nric,
    agentCode,
    companyName: isEBLTC ? companyName : ""
  }

  return {
    transactionId,
    // campaignCode: campaignCode,
    content: JSON.stringify(content),
    cc: campaignCode ? campaignCode : 'MLO1',
    dropPoint: isEBLTC ? 'QNB_MLTC_EBLTC_CONTACT_US' : 'QNB_MLTC_CONTACT_US'
  }
}

export const getTimestamp = () => {
  const pad = (i: number) => { return (i < 10) ? '0' + i : i; };

  const d = new Date();
  const Y = d.getFullYear();
  const m = d.getMonth() + 1;
  const D = d.getDate();
  const H = d.getHours();
  const M = d.getMinutes();
  const S = d.getSeconds();
  const ms = ('00' + d.getMilliseconds()).slice(-3);
  return `` + Y + pad(m) + pad(D) + pad(H) + pad(M) + pad(S) + ms;
}


export const ContactForm = () => {

  const history = useHistory();
  const params = new URLSearchParams(window.location.href.split('?')[1]);
  const variant = params.get('variant') || 'needHelp';

  const title = variant === 'needHelp' ? 'Need Help?' : 'Thank you for your interest in this plan.'
  const description = variant === 'needHelp' ? 'Leave your contact and we will reach out to assist you' : `We're unable to proceed with your application based on the details you provided. But fret not, simply fill up the form below and we will be in touch to assist you soon!`;
  const DPOUrl = "https://singlife.com/en/pdpa#DPO";

  const theme = useTheme();

  const dispatch = useAvivaDispatch();
  const retrievedMyInfo = useAvivaSelector(state => state.mltcR3Step4.retrievedMyInfo);
  const contactInfo = useAvivaSelector((state) => state.mltcR3Contact.contact);
  const quoteRawResponse = useAvivaSelector(state => state.mltcR3Step2.quoteRawResponse);
  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);  
  const basicDetails = useAvivaSelector(state => state.mltcR3Step1.basicDetails);
  const age = basicDetails?.dob
    ? Helpers.getAge(
        new Date(
          `${basicDetails.dob.month}/${basicDetails.dob.day}/${basicDetails.dob.year}`,
        ),
      )
    : null;
  const isEBLTC = campaignDetail?.code === "EBLTC" && campaignDetail?.status !== "EXPIRED" && age !== null && (age >= 31 && age <= 40);
  const companyName = campaignDetail?.companyName || "";
  const hasMyInfo = useAvivaSelector(state => state.mltcR3Step4.hasMyInfo);
  const myInfoForm = useAvivaSelector(state => state.mltcR3Step4.myInfoForm);
  const transactionIdFromStore = useAvivaSelector(state=>state.mltcR3Step2.transactionId);
  const personRawResponse = useAvivaSelector(state => state.mltcR3Step4.myInfoRawResponse);

  const [isVulnerable, setIsVulnerable] = useState<boolean | undefined>(undefined);
  const [isRental, setIsRental] = useState<boolean | undefined>(undefined);
  const [aaTracking,setAATracking] = useState<string[]>([]);
  const [aaTrackingStart,setAATrackingStart] = useState<string[]>([]);

  const [formSubmited, setFormSubmited] = useState<boolean>(false)
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const urlParams = Helpers.getURLSearchParams();
  const agentCode = useAvivaSelector(state=>state.urlAnalytic?.param?.arc) || null;

  const { values, errors, patchValue, handleChange, formValid } = useForm(
    new ContactFormModel(contactInfo),
    () => { },
    validate
  );
  const ContactUsUrl = get(window, 'AVIVA_VARIABLE_CONFIG.avivaContactUsUrl');


const trackOnload = (formName:string): void => {
  if(!aaTracking.includes(formName)){
    setAATracking([...aaTracking,formName]);

    const GA = GAAviva.getInstance();
    let { form, acquisition, page } = GA.digitalData;
    form.name = formName;

    GA.trackPageLoad({ form, acquisition, page });
  }
}
const trackOnStart = (formName:string): void => {
  if(!aaTrackingStart.includes(formName)){
    setAATrackingStart([...aaTrackingStart,formName]);
    const GA = GAAviva.getInstance();
    let { form } = GA.digitalData;
    form.name = formName;
    GA.trackPageStart();
  }
}

const trackCTA = () => {
  const GA = GAAviva.getInstance();
  let { cta } = GA.digitalData;
  cta = {
    type: 'MLTC Contact Form', 
    text: 'Submit'
  };
  GA.trackCTAClick({ cta });
}

const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    trackCTA();
    GA.trackFormCompleted({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = [error];
  GA.trackFormFailed({ form });
}



  const onInputChange = (e:any) => {
    console.log("onInputChange",e.target.value);
    trackOnStart(variant === 'needHelp' ? 'mltc_contact_need_help_form' : 'mltc_contact_ineligible_form');
    handleChange(e);
  }

  const onSubmit = async () => {
    dispatch(setLoadingState(true));
    setFormSubmited(true);
    dispatch(updateContact(values));
    let transactionId = (personRawResponse && transactionIdFromStore) ? transactionIdFromStore : Number(getTimestamp());
    let productName = 'Singlife CareShield Standard';
    if (quoteRawResponse) {
      const planQuotation = new SQSPocketResponse(quoteRawResponse);
      productName = planQuotation.planName || 'Singlife CareShield Standard';
    }
    console.log("values", values);
    const payload: any = createContactRequest(values, isVulnerable, transactionId, isRental, campaignDetail?.campaignCode, productName, agentCode, isEBLTC, companyName);

    /*LCB1*/
    if (campaignDetail?.code === CIMB_Code) {
      let planName = 'Singlife CareShield Standard';
      if (quoteRawResponse) {
        const planQuotation = new SQSPocketResponse(quoteRawResponse);
        console.log('plan name', planQuotation.planName);
        planName = planQuotation.planName || '';
      }
      const request = {
        "templateCode": isRental ? 'rental_property' : (isVulnerable ? 'trusted_individual' : 'generic'),
        "productName": planName,
        "excelData": {
          "salutation": values['salutation'],
          "givenName": values['givname'],
          "familyName": values['surname'],
          "mobileNumber": values['mobileNum'],
          "email": values['emailId']
        }
      }
      try {
        const response = await postContactCIMBMltc(request);
        console.debug(response);
        trackFormSubmmit();
        Helpers.clearCached();
        dispatch(setLoadingState(false));

      } catch (error) {
        trackFormSubmmit(get(error, 'message'));
        console.error(error);
        dispatch(setLoadingState(false));
      }

    } else {
      try {
        const response = await postContactMltc(payload);
        console.debug(response);
        trackFormSubmmit();
        Helpers.clearCached();
        dispatch(setLoadingState(false));

      } catch (error) {
        trackFormSubmmit(get(error, 'message'));
        console.error(error);
        dispatch(setLoadingState(false));
      }
    }

  };


  useEffect(() => {
    if (!isPageLoaded) {
      trackOnload(variant === 'needHelp' ? 'mltc_contact_need_help_form' : 'mltc_contact_ineligible_form');
      // Retrieve campaign code from url
      const urlCC = urlParams.get('cc');
      const dataIsLoaded = (!!urlCC && !!campaignDetail) || !urlCC;

      if (campaignDetail) {
        /*LCB1*/
        if (campaignDetail?.code === CIMB_Code) {
          patchValue({
            isAvivaCustomer: 'N'
          });

          if (quoteRawResponse) {
            const planQuotation = new SQSPocketResponse(quoteRawResponse);
            console.log('plan name', planQuotation.planName);
          }
        }
      }

      if (basicDetails) {
        patchValue({
          dob: Helpers.parseDobString(basicDetails.dob)
        })
      }
      if (isVulnerable === undefined) {
        const vulnerable = urlParams.get('vulnerable');
        setIsVulnerable(vulnerable === 'true');
      }
      if (isRental === undefined) {
        const rental = urlParams.get('rental');
        setIsRental(rental === 'true');
      }

      if (dataIsLoaded) setIsPageLoaded(true);
      if(hasMyInfo){
        patchValue({
          fullname: myInfoForm?.fullName,
          mobileNum: myInfoForm?.mobileNum,
          emailId: myInfoForm?.emailId,
          salutation: myInfoForm?.salutation,
        });
      }
    }
  }, [hasMyInfo,myInfoForm, patchValue, isPageLoaded, urlParams, basicDetails, isRental, isVulnerable, campaignDetail, quoteRawResponse]);


  return (
    !formSubmited ? (
      <>
        <ContacFormTitleContainer>
          <TitleAndDescription
            title={title}
            description={description}
            margin="0 0 64px 0"
          />
        </ContacFormTitleContainer>
        <ContacFormContainer>
          <ContactInputContainer>
            <div>
              <InputGroup
                labelColor='#434343'
                id="salutation"
                name="salutation"
                label="Salutation"
                inputType="dropdown"
                onChange={onInputChange}
                placeholder="Please select"
                dropdownProps={{
                  value: values['salutation'],
                  source: [
                    { id: 'Mr', label: 'Mr' },
                    { id: 'Mrs', label: 'Mrs' },
                    { id: 'Ms', label: 'Ms' },
                    { id: 'Master', label: 'Master' },
                    { id: 'Mdm', label: 'Mdm' },
                    { id: 'Dr', label: 'Dr' },
                  ],
                }}
              />
            </div>
            <InputGroup
              labelColor='#434343'
              id="fullname"
              name="fullname"
              label="Full Name"
              placeholder="John"
              onChange={(e: any) => {
                let formatted_name = e.target.value.replace(/[^a-z\s]/gi, '');
                onInputChange({ target: { value: formatted_name, name: e.target.name } });
              }}
              invalid={!isEmpty(errors?.fullname?.message)}
              errorMessage={errors?.fullname?.message}
              inputProps={{
                textAlign: 'left',
                value: values['fullname'],
              }} 
              inputType={'input'} 
              />
            <InputGroup
              labelColor='#434343'
              id="mobileNum"
              name="mobileNum"
              label="Mobile Number"
              placeholder=""
              mobCode="+65"
              onChange={(e)=>{
                if(/^(8|9){1}[0-9]{0,7}$/.test(e.target.value) || e.target.value === "")
                  onInputChange(e)
                }
                }
              invalid={!Boolean(errors?.mobileNum?.isValid)}
              errorMessage={errors?.mobileNum?.message || "Enter a valid 8-digit Singapore mobile number"}
              inputProps={{
                textAlign: 'left',
                value: values['mobileNum'],
              }}
              inputType={'input'} 
              onBlurShowError
              />
            <InputGroup
              labelColor='#434343'
              id="emailId"
              name="emailId"
              label="Email"
              placeholder="John@singlife.com"
              onChange={onInputChange}
              invalid={!isEmpty(errors?.emailId?.message)}
              errorMessage={errors?.emailId?.message}
              inputProps={{
                textAlign: 'left',
                value: values['emailId'],
              }}
              onBlurShowError={true} 
              inputType={'input'} 
              />
          </ContactInputContainer>
          <FormActionContainer>
            <Button onClick={() => {
              history.goBack();
            }} variant="secondary" label="Back" width="180px" />
            <Button
              width="180px"
              variant="default"
              label="Submit"
              disabled={!formValid}
              onClick={onSubmit}
            />
          </FormActionContainer>
          <SubmitNote theme={theme}>
            By clicking &quot;Submit&quot;, you consent to Singapore Life Ltd (&quot;Singlife&quot;) 
            collecting, using, disclosing or transferring your personal data to 
            Singlife related companies, third party service providers or intermediaries 
            (including your financial adviser, where applicable) whether located in 
            Singapore or elsewhere, to assist you with your application. You can refer to
            &nbsp;<Link theme={themeDefault} target="_blank" rel="noopener noreferrer" url={ContactUsUrl} text={`Singlife's Data Protection Notice`} /> 
            &nbsp;for further details. If you wish to withdraw your consent, you may contact Singlife at 
            &nbsp;<Link theme={themeDefault} url="mailto:cs_life@singlife.com" text="cs_life@singlife.com" /> 
            &nbsp;or  
            &nbsp;<Link theme={themeDefault} url="tel:+6568279933" text="+65 6827 9933" /> 
          </SubmitNote>
          <Contactus/>
        </ContacFormContainer>
      </>) : <SubmitSuccess />
  );
};
export default ContactForm